/**
 * DE-310 — which board a device is, and how sure we are.
 *
 * ORDER OF TRUTH: shadow → thing attribute → assumption. The middle step is the
 * one that is easy to skip and expensive to get wrong.
 *
 * A freshly provisioned R1035 sitting in `artomeui-unassigned` has the
 * `deviceType` thing attribute from its very first registration (DE-308) but no
 * shadow at all until telemetry runs — and a guard that consulted only the
 * database would count it as the default board and wave an r110x-only release
 * straight through. So: **never fall through to the assumption while an
 * attribute exists.**
 *
 * The assumption itself is sound today — every unit in the field is an R1125 —
 * but an operator has to be able to tell a fact from a default, which is why
 * `source` travels with the value instead of being flattened away.
 */

/** Where the answer came from, in descending order of authority. */
export type HardwareSource = 'reported' | 'attribute' | 'assumed';

export interface ResolvedHardware {
  /** Canonical device type, e.g. `artomeui-r110x`. */
  id: string;
  /** What a human reads, e.g. `R1125-10`. From the registry, never formatted. */
  displayName: string;
  source: HardwareSource;
  /** True unless the value is a fact. Drives the "(assumed)" suffix. */
  assumed: boolean;
}

/**
 * The fallback used when nothing is known.
 *
 * Deliberately a named constant rather than a literal at each call site: when
 * the assumed count reaches zero this becomes a warning instead of a default,
 * and that must be one edit rather than a search.
 */
export const ASSUMED_DEVICE_TYPE = 'artomeui-r110x';

export interface HardwareTypeRow {
  id: string;
  display_name: string;
  active?: boolean;
}

/**
 * @param reported  `devices.device_type` — the shadow-derived generated column.
 * @param attribute the `deviceType` IoT thing attribute, when known.
 * @param registry  rows from GET /hardware-types.
 */
export function resolveHardware(
  reported: string | null | undefined,
  attribute: string | null | undefined,
  registry: HardwareTypeRow[] | undefined,
): ResolvedHardware {
  const id = reported || attribute || ASSUMED_DEVICE_TYPE;
  const source: HardwareSource = reported ? 'reported' : attribute ? 'attribute' : 'assumed';
  return {
    id,
    displayName: hardwareDisplayName(id, registry),
    source,
    assumed: source === 'assumed',
  };
}

/**
 * Registry display name, falling back to the canonical id.
 *
 * Falls back to the ID rather than to "Unknown": a board the registry has not
 * heard of is still a real answer the device gave us, and hiding it behind
 * "Unknown" would make a registry gap look like a device problem.
 */
export function hardwareDisplayName(
  id: string | null | undefined,
  registry: HardwareTypeRow[] | undefined,
): string {
  if (!id) return '—';
  return registry?.find((h) => h.id === id)?.display_name ?? id;
}

/** `R1125-10 (assumed)` when inferred, plain otherwise. */
export function hardwareLabel(hw: ResolvedHardware): string {
  return hw.assumed ? `${hw.displayName} (assumed)` : hw.displayName;
}

/** Longer explanation for a tooltip — carries the canonical id and the source. */
export function hardwareTitle(hw: ResolvedHardware): string {
  switch (hw.source) {
    case 'reported':
      return `${hw.id} — reported by the device`;
    case 'attribute':
      return `${hw.id} — from the IoT thing attribute; the device has not reported telemetry yet`;
    default:
      return `${hw.id} — assumed. This device has reported no hardware type and carries no `
        + `thing attribute, so the fleet default is shown. It is not a fact.`;
  }
}

export interface HardwareBucket {
  id: string;
  displayName: string;
  things: string[];
  /** Members whose type is an assumption rather than a fact. */
  assumed: number;
}

/** Group members by resolved hardware — what the deploy guard counts. */
export function bucketByHardware(
  things: string[],
  reportedBy: Record<string, string | null | undefined>,
  attributeBy: Record<string, string | null | undefined>,
  registry: HardwareTypeRow[] | undefined,
): HardwareBucket[] {
  const by = new Map<string, HardwareBucket>();
  for (const t of things) {
    const hw = resolveHardware(reportedBy[t], attributeBy[t], registry);
    let b = by.get(hw.id);
    if (!b) {
      b = { id: hw.id, displayName: hw.displayName, things: [], assumed: 0 };
      by.set(hw.id, b);
    }
    b.things.push(t);
    if (hw.assumed) b.assumed += 1;
  }
  return [...by.values()].sort((a, b) => b.things.length - a.things.length);
}

export type DeployVerdict =
  | { kind: 'ok' }
  | { kind: 'warn'; covered: number; total: number; buckets: HardwareBucket[]; message: string }
  | { kind: 'block'; buckets: HardwareBucket[]; message: string };

/**
 * Can this OS artifact be deployed to these members?
 *
 * block — the release matches NO member. Dispatching would fail every device.
 * warn  — mixed group, the release covers only some of it. Legitimate (the rest
 *         simply refuse, cheaply, since DE-309) but the operator should know
 *         the counts before clicking rather than discover them afterwards.
 * ok    — every member matches, or there is no OS part to check.
 */
export function evaluateDeploy(
  osDeviceType: string | null | undefined,
  buckets: HardwareBucket[],
): DeployVerdict {
  if (!osDeviceType || buckets.length === 0) return { kind: 'ok' };
  const total = buckets.reduce((n, b) => n + b.things.length, 0);
  const match = buckets.find((b) => b.id === osDeviceType);
  const covered = match?.things.length ?? 0;
  const names = buckets.map((b) => `${b.displayName} ×${b.things.length}`).join(', ');

  if (covered === 0) {
    return {
      kind: 'block',
      buckets,
      message: `This release's OS image is for ${osDeviceType}, which matches none of the `
        + `${total} device(s) in this group. The group contains: ${names}.`,
    };
  }
  if (covered < total) {
    return {
      kind: 'warn',
      covered,
      total,
      buckets,
      message: `This release's OS image is for ${osDeviceType} and covers ${covered} of `
        + `${total} device(s). The rest will refuse it before downloading anything. `
        + `The group contains: ${names}.`,
    };
  }
  return { kind: 'ok' };
}

// ── DE-312: coverage ────────────────────────────────────────────────────────

/**
 * Which boards a release actually carries an OS image for.
 *
 * `os` is the legacy single entry and means artomeui-r110x when nothing else
 * says otherwise (DE-311), so a release composed before the map still reports
 * honest coverage rather than none.
 */
export function releaseCoverage(
  parts: {
    os?: { deviceType?: string | null } | null;
    osByDeviceType?: Record<string, unknown> | null;
  } | null | undefined,
  legacyOsDeviceType?: string | null,
): string[] {
  const out = new Set<string>();
  for (const k of Object.keys(parts?.osByDeviceType ?? {})) out.add(k);
  const legacy = parts?.os ? (parts.os.deviceType || legacyOsDeviceType) : null;
  if (legacy) out.add(legacy);
  return [...out].sort();
}

export type CoverageVerdict =
  | { kind: 'ok'; covered: string[] }
  | { kind: 'partial'; covered: string[]; uncovered: HardwareBucket[]; message: string }
  | { kind: 'none'; covered: string[]; uncovered: HardwareBucket[]; message: string };

/**
 * Does this release cover the hardware actually in the target?
 *
 * `none`    — no member matches. Dispatching updates nothing.
 * `partial` — some boards are uncovered. LEGITIMATE — rolling one board type
 *             out first is a normal thing to want — so it asks for an explicit
 *             confirmation and names exactly which devices will not be updated,
 *             rather than blocking it.
 * `ok`      — every board in the target is covered.
 *
 * Counting is the point. "Some devices won't update" is not actionable; "3 of
 * 27, all R1035-10" is.
 */
export function evaluateCoverage(
  coverage: string[],
  buckets: HardwareBucket[],
): CoverageVerdict {
  if (buckets.length === 0 || coverage.length === 0) return { kind: 'ok', covered: coverage };
  const uncovered = buckets.filter((b) => !coverage.includes(b.id));
  if (uncovered.length === 0) return { kind: 'ok', covered: coverage };

  const n = uncovered.reduce((a, b) => a + b.things.length, 0);
  const total = buckets.reduce((a, b) => a + b.things.length, 0);
  const named = uncovered.map((b) => `${b.displayName} ×${b.things.length}`).join(', ');

  if (n === total) {
    return {
      kind: 'none',
      covered: coverage,
      uncovered,
      message: `This release carries no image for anything in this group. It contains `
        + `${named}; the release covers ${coverage.join(', ')}.`,
    };
  }
  return {
    kind: 'partial',
    covered: coverage,
    uncovered,
    message: `${n} of ${total} device(s) will NOT be updated — this release carries no image `
      + `for ${named}. They will refuse it before downloading anything. Continue only if you `
      + `mean to roll out one board type first.`,
  };
}
